
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { User } from '../../../types';
import { X, User as UserIcon, Mail, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../../ui/Button';

interface EditProfileModalProps {
    user: User;
    onClose: () => void;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({ user, onClose }) => {
    const [name, setName] = useState(user.name);
    const [email, setEmail] = useState(user.email || '');

    const handleSave = () => {
        console.log("Saving profile:", { name, email });
        // Here you would typically call an API to update the user
        onClose();
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm"
        >
            <motion.div
                initial={{ scale: 0.9, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 10 }}
                className="bg-white dark:bg-[#15171C] rounded-2xl shadow-2xl w-full max-w-md p-6 relative border border-stone-200 dark:border-[#2D3142]"
            >
                <button onClick={onClose} className="absolute top-4 right-4 text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                    <X size={20} />
                </button>
                <h3 className="font-serif text-2xl text-stone-900 dark:text-white mb-6">Edit Profile</h3>

                <div className="flex flex-col items-center mb-8">
                    <div className="relative">
                        <div className="w-24 h-24 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center text-4xl font-serif font-bold text-stone-800 dark:text-white">
                            {user.name.charAt(0)}
                        </div>
                        <button className="absolute bottom-0 right-0 w-8 h-8 bg-stone-900 dark:bg-white text-white dark:text-black rounded-full flex items-center justify-center border-2 border-white dark:border-[#15171C] hover:scale-110 transition-transform">
                            <Camera size={14} />
                        </button>
                    </div>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">Full Name</label>
                        <div className="relative mt-1">
                            <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full pl-10 p-3 bg-stone-50 dark:bg-white/5 rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">Email Address</label>
                         <div className="relative mt-1">
                            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-10 p-3 bg-stone-50 dark:bg-white/5 rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-8 mt-4 border-t border-stone-100 dark:border-white/10">
                    <Button onClick={onClose} variant="outline">Cancel</Button>
                    <Button onClick={handleSave}>Save Changes</Button>
                </div>
            </motion.div>
        </motion.div>
    );
};
